import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ts, P } from '../api';
import Avatar from '../Avatar';
import { useAppCalls } from '../AppCalls';
import {
  Page, Section, Stat, Table, Td, Pill, Loading, ErrorNote, Empty, ROLE_LABELS, useLoad, useTS, when,
} from '../ui';

// Owner Control: one live page for the office. It refetches when the shared
// socket reports presence or an owner_event, so it never needs a manual
// refresh. Every action is enforced by the server.
const STATE = {
  available: ['green', 'Available'],
  busy: ['amber', 'Busy'],
  away: ['amber', 'Away'],
  break: ['amber', 'On break'],
  off_duty: ['gray', 'Off duty'],
  on_call: ['blue', 'On a call'],
  offline: ['gray', 'Offline'],
  suspended: ['red', 'Suspended'],
};

function fmtSecs(s) {
  if (s == null) return '';
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function TSControl() {
  const session = useTS();
  const snap = useLoad(() => ts.control());
  const { subscribe } = useAppCalls();
  const [tick, setTick] = useState(0);
  const timer = useRef(null);
  const canManage = session.can(P.STAFF);

  // Coalesce bursts of events into one refetch.
  useEffect(() => subscribe((msg) => {
    if (!['presence', 'owner_event', 'directory_changed'].includes(msg.type)) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => snap.reload(), 400);
  }), [subscribe]); // eslint-disable-line react-hooks/exhaustive-deps
  // A safety refresh every 30s, and a 1s clock for call timers.
  useEffect(() => { const t = setInterval(() => snap.reload(), 30000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 1000); return () => clearInterval(t); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  const act = async (fn, ok) => {
    try { const r = await fn(); if (ok) toast.success(ok); snap.reload(); return r; } catch (e) { toast.error(e.message); return null; }
  };

  if (snap.loading && !snap.data) return <Loading />;
  if (snap.error && !snap.data) return <ErrorNote error={snap.error} />;
  const d = snap.data;
  const t = d.totals;
  const loadedAt = new Date(d.generated_at).getTime();

  return (
    <Page title="Owner Control" subtitle="Your office right now. Updates live.">
      <ErrorNote error={snap.error} />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="ts-control-totals">
        <Stat label="App open" value={t.app_open} />
        <Stat label="On a call" value={t.on_call} />
        <Stat label="Available" value={t.available} />
        <Stat label="Offline" value={t.offline} />
        <Stat label="Pending callbacks" value={t.pending_callbacks} hint={t.unassigned_callbacks ? `${t.unassigned_callbacks} unassigned` : undefined} />
        <Stat label="Missed calls today" value={t.missed_calls_today} />
        <Stat label="Open tasks" value={t.open_tasks} />
        <Stat label="Unassigned leads" value={t.unassigned_leads} />
        <Stat label="Chat messages today" value={t.chat_messages_today} />
        <Stat label="AI actions today" value={t.ai_actions_today} />
      </div>

      <Section title="Team">
        {d.members.length === 0 ? <Empty>No staff yet.</Empty> : (
          <Table head={['Person', 'State', 'Call', 'Number', 'Work', 'Devices', '']}>
            {d.members.map(m => {
              const [tone, label] = STATE[m.state] || STATE.offline;
              const secs = m.call?.seconds != null ? m.call.seconds + Math.floor((Date.now() - loadedAt) / 1000) : null;
              return (
                <tr key={m.id} data-testid={`ts-control-row-${m.id}`}>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Avatar id={m.id} name={m.full_name} version={m.avatar_version} />
                      <div>
                        <div className="font-medium">{m.full_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {ROLE_LABELS[m.role]}{m.extension ? ` · Ext. ${m.extension}` : ''}{m.department ? ` · ${m.department}` : ''}
                        </div>
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <Pill tone={tone} data-testid={`ts-control-state-${m.id}`}>{label}</Pill>
                    {m.status_message && <div className="text-xs mt-1 italic" data-testid={`ts-control-msg-${m.id}`}>“{m.status_message}”</div>}
                    {m.work_status_by && <div className="text-xs text-amber-700 mt-1">Set by {m.work_status_by_name || 'a manager'}</div>}
                    {m.status === 'active' && (m.role !== 'owner' || m.id === session.member.id) && (
                      <select className="mt-1 text-xs border rounded px-1 py-0.5 bg-background" value=""
                        aria-label={`Change ${m.full_name}'s status`} data-testid={`ts-control-setstatus-${m.id}`}
                        onChange={e => e.target.value && act(() => ts.setMemberStatus(m.id, { status: e.target.value }), `${m.full_name} is now ${STATE[e.target.value]?.[1] || e.target.value}`)}>
                        <option value="">Change status…</option>
                        {['available', 'busy', 'away', 'break', 'off_duty'].map(k => <option key={k} value={k}>{STATE[k][1]}</option>)}
                      </select>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">{m.app_open ? 'App open' : `Last seen ${when(m.last_seen_at)}`}</div>
                  </Td>
                  <Td>
                    {m.call ? (m.call.internal ? <span className="text-xs">Office call</span> : (
                      <div className="text-xs">
                        <div>{m.call.direction === 'inbound' ? 'From' : 'To'} {m.call.customer}</div>
                        {secs != null && <div className="font-mono" data-tick={tick}>{fmtSecs(secs)}</div>}
                      </div>
                    )) : <span className="text-xs text-muted-foreground">—</span>}
                  </Td>
                  <Td className="text-xs">{m.number ? m.number.display || m.number.e164 : <span className="text-muted-foreground">None</span>}</Td>
                  <Td className="text-xs whitespace-nowrap">
                    <div>{m.open_callbacks} callbacks</div>
                    <div>{m.open_tasks} tasks · {m.open_leads} leads</div>
                  </Td>
                  <Td className="text-xs">
                    {m.devices.length === 0 ? <span className="text-muted-foreground">{m.activation_status === 'code_issued' ? 'Code issued' : 'Not activated'}</span>
                      : m.devices.map(dv => <div key={dv.id}>{dv.model || dv.platform}</div>)}
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    {canManage && m.role !== 'owner' && m.id !== session.member.id && (
                      m.status === 'active' ? (
                        <>
                          <Button size="sm" variant="ghost" data-testid={`ts-control-logout-${m.id}`}
                            onClick={() => act(() => ts.forceLogout(m.id), `${m.full_name} signed out everywhere`)}>Sign out</Button>
                          <Button size="sm" variant="ghost" className="text-red-500" data-testid={`ts-control-suspend-${m.id}`}
                            onClick={() => {
                              if (window.confirm(`Suspend ${m.full_name}? Their calls end and every device is signed out now.`)) {
                                act(() => ts.setStatus(m.id, 'suspend'), `${m.full_name} suspended`);
                              }
                            }}>Suspend</Button>
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => act(() => ts.setStatus(m.id, 'reactivate'), 'Reactivated')}>Reactivate</Button>
                      )
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
        <p className="text-xs text-muted-foreground">{d.note} Codes, devices, numbers and permissions are managed on the Staff page.</p>
      </Section>

      <Section title="Recent AI activity">
        {d.ai_activity.length === 0 ? <Empty>No AI actions yet.</Empty> : (
          <Table head={['When', 'Who', 'Action', 'Status']}>
            {d.ai_activity.map(a => (
              <tr key={a.id}><Td>{when(a.created_at)}</Td><Td>{a.member_name || '—'}</Td><Td>{a.tool}</Td><Td>{a.status}</Td></tr>
            ))}
          </Table>
        )}
      </Section>
    </Page>
  );
}
